const {Promise} = globalThis;

/** Ein simples Mixin, um Loading-Prozesse zu tracken. Wenn man es mit {@link Eventable} verwendet,
 * werden die Events {@link Loadable#event:load|load} und {@link Loadable#event:loaded|loaded} gefeuert.
 * @mixin Loadable
 */
const Loadable = superClass => class extends superClass
{
    #loadedPromise = null;
    #resolvePromise = null;
    #load = 0;
    #loaded = 0;
    #loadComplete = false;

    _fireLoadEvents = false;

    constructor(...params)
    {
        super(...params);
        this.resetLoadProgress();
        this._fireLoadEvents = typeof this.fire === 'function';
    }

    get _load()
    {
        console.warn('_load will be private.');
        return this.#load;
    }

    get _loadComplete()
    {
        console.warn('_loadComplete will be private, please use isLoaded method.');
        return this.#loadComplete;
    }

    /** Liefert im Gegensatz zur ``isLoaded`` Methode ein Promise zurück.
     * @memberof Loadable#
     * @type {Promise}
     */
    get loaded()
    {
        return this.#loadedPromise;
    }

    /** Fügt eine Last hinzu.
     * @memberof Loadable#
     * @param {number} [load=1] - Die Menge der zu ladenden Einheiten
     * @param {Promise} [promise] - Ein optionales Promise, welche die Last automatisch auflöst
     * @return {this}
     */
    addLoad(load = 1, promise)
    {
        this.#load += load;
        if (promise instanceof Promise)
        {
            promise.then(() => this.completeLoad(load));
        }
        return this;
    }

    /** Löst eine Last auf.
     * @memberof Loadable#
     * @param {number} [load=1] - Die Menge der geladenen Einheiten
     * @return {this}
     */
    completeLoad(load = 1)
    {
        if (this.getLoadProgress() >= 1) { return this; }

        this.#loaded += load;

        const progress = this.getLoadProgress();

        if (this._fireLoadEvents)
        {
            /**
             * @event Loadable#load
             * @property {number} progress
             */
            this.fire('load', {progress});
        }

        if (progress >= 1)
        {
            this.#loadComplete = true;
            if (this._fireLoadEvents)
            {
                /**
                 * @event Loadable#loaded
                 */
                this.fire('loaded');
            }
            this.#resolvePromise();
        }

        return this;
    }

    /** Nullt den Fortschritt.
     * @memberof Loadable#
     * @return {this}
     */
    resetLoadProgress()
    {
        this.#load = 0;
        this.#loaded = 0;
        this.#loadComplete = false;
        this.#loadedPromise = new Promise((resolve) =>
        {
            this.#resolvePromise = resolve;
        });
        return this;
    }

    /** Liefert den Lade-Fortschritt.
     * @memberof Loadable#
     * @return {number}
     */
    getLoadProgress()
    {
        if (this.#load === 0) { return 0; }
        return this.#loaded / this.#load;
    }

    /** Ist wichtig für den {@link LoaderAdpater} und seiner cancel-Methode. Hier sollte ein Abbruch
     * des Dateiladens implementiert werden.
     * @memberof Loadable#
     */
    cancelLoading() {}

    /** Gibt zurück, ob alle Einheiten geladen wurden.
     * @memberof Loadable#
     * @return {boolean}
     */
    isLoaded()
    {
        return this.#loadComplete;
    }
};

Loadable.sorting = 4;

export default Loadable;
